import React, { useState, useEffect, useCallback } from 'react';
import { portfolioReportsAPI } from '../../services/api';

const MONTH_NAMES = {
  '01': 'January', '02': 'February', '03': 'March', '04': 'April',
  '05': 'May', '06': 'June', '07': 'July', '08': 'August',
  '09': 'September', '10': 'October', '11': 'November', '12': 'December',
};
const QUARTER_MONTHS = { 1: 'March', 2: 'June', 3: 'September', 4: 'December' };

// Server timestamps are naive UTC ("YYYY-MM-DD HH:MM:SS.ffffff"); show local time.
const formatEventTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts.replace(' ', 'T').replace(/(\.\d{3})\d+$/, '$1') + 'Z');
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatPeriodLabel = (period) => {
  if (!period) return '';
  if (period.includes('-Q')) {
    const [year, q] = period.split('-Q');
    return `Q${q} ${year} (${QUARTER_MONTHS[parseInt(q)] || ''})`;
  }
  const [year, month] = period.split('-');
  return `${MONTH_NAMES[month] || month} ${year}`;
};

/**
 * Modal for managing per-portfolio publish status for a period.
 * Drummond users can toggle which portfolios are visible to clients.
 */
const PublishModal = ({ isOpen, onClose, quarter, isDrummondUser }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandPortfolios, setBrandPortfolios] = useState({});
  const [publishedSet, setPublishedSet] = useState(new Set());
  const [initialSet, setInitialSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({ drummond: true });
  // Period being managed — defaults to the app's selected quarter but can be
  // switched from the dropdown so any period's publishing is manageable here.
  const [selectedQuarter, setSelectedQuarter] = useState(quarter);
  const [quarterOptions, setQuarterOptions] = useState([]);
  // Publish / unpublish audit trail for the selected period
  const [showHistory, setShowHistory] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Pending diff awaiting confirmation: { added: [codes], removed: [codes] }
  const [confirmDiff, setConfirmDiff] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const loadEvents = useCallback(async (q) => {
    if (!q || !isDrummondUser) return;
    setLoadingEvents(true);
    try {
      const res = await portfolioReportsAPI.getPublishEvents(q, 300);
      setEvents(res.events || []);
    } catch (err) {
      console.error('Error loading publish history:', err);
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [isDrummondUser]);

  useEffect(() => {
    if (isOpen && showHistory) loadEvents(selectedQuarter);
  }, [isOpen, showHistory, selectedQuarter, loadEvents]);

  const loadData = useCallback(async (q) => {
    if (!q || !isDrummondUser) return;
    setLoading(true);
    try {
      const [portfolioRes, brandRes, publishedRes] = await Promise.all([
        portfolioReportsAPI.getPortfolios(),
        portfolioReportsAPI.getBrands().catch(() => null),
        portfolioReportsAPI.getPublishedPortfolios(q),
      ]);

      setPortfolios(portfolioRes.portfolios || []);
      setBrands(brandRes?.brands || []);

      const codes = new Set(publishedRes.published_portfolios || []);
      setPublishedSet(codes);
      setInitialSet(new Set(codes));
    } catch (err) {
      console.error('Error loading publish data:', err);
    } finally {
      setLoading(false);
    }
  }, [isDrummondUser]);

  useEffect(() => {
    if (isOpen) {
      setSelectedQuarter(quarter);
      loadData(quarter);
      setBrandPortfolios({});
      setExpandedGroups({ drummond: true });
      setShowHistory(false);
      setEvents([]);
      setConfirmDiff(null);
      setSaveError(null);
      // All periods (quarterly + monthly) for the dropdown; keep the current
      // quarter in the list even if the endpoint doesn't return it.
      portfolioReportsAPI.getAvailableQuarters('both')
        .then(res => {
          const periods = res.periods && res.periods.length > 0
            ? res.periods.map(p => p.value)
            : (res.quarters || []);
          if (quarter && !periods.includes(quarter)) periods.unshift(quarter);
          setQuarterOptions(periods);
        })
        .catch(() => setQuarterOptions(quarter ? [quarter] : []));
    }
  }, [isOpen, quarter, loadData]);

  const handleQuarterChange = (q) => {
    if (q === selectedQuarter) return;
    if (hasChanges() && !window.confirm('Discard unsaved publishing changes for this period?')) {
      return;
    }
    setSelectedQuarter(q);
    setBrandPortfolios({});
    setExpandedGroups({ drummond: true });
    loadData(q);
  };

  const toggleGroup = async (groupKey) => {
    const willExpand = !expandedGroups[groupKey];
    setExpandedGroups(prev => ({ ...prev, [groupKey]: willExpand }));

    if (willExpand && groupKey !== 'drummond' && !brandPortfolios[groupKey]) {
      setLoadingBrands(prev => ({ ...prev, [groupKey]: true }));
      try {
        const res = await portfolioReportsAPI.getBrandPortfolios(groupKey);
        setBrandPortfolios(prev => ({ ...prev, [groupKey]: res.portfolios || [] }));
      } catch (err) {
        console.error(`Error loading portfolios for ${groupKey}:`, err);
        setBrandPortfolios(prev => ({ ...prev, [groupKey]: [] }));
      } finally {
        setLoadingBrands(prev => ({ ...prev, [groupKey]: false }));
      }
    }
  };

  const togglePortfolio = (code) => {
    setPublishedSet(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const toggleAll = (portfolioList, published) => {
    setPublishedSet(prev => {
      const next = new Set(prev);
      portfolioList.forEach(p => {
        if (published) {
          next.add(p.portfolio_code);
        } else {
          next.delete(p.portfolio_code);
        }
      });
      return next;
    });
  };

  const hasChanges = () => {
    if (publishedSet.size !== initialSet.size) return true;
    for (const code of publishedSet) {
      if (!initialSet.has(code)) return true;
    }
    return false;
  };

  // Saving here is publishing: it changes what clients can see. Show the exact
  // diff and require an explicit confirm before anything is sent.
  const requestSave = () => {
    const added = [...publishedSet].filter(c => !initialSet.has(c)).sort();
    const removed = [...initialSet].filter(c => !publishedSet.has(c)).sort();
    if (added.length === 0 && removed.length === 0) return;
    setConfirmDiff({ added, removed });
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      await portfolioReportsAPI.publishPortfoliosBulk(selectedQuarter, [...publishedSet]);
      setInitialSet(new Set(publishedSet));
      setConfirmDiff(null);
      onClose(true);
    } catch (err) {
      console.error('Error saving publish status:', err);
      setSaveError('Publishing changes could not be saved. Nothing has changed for clients.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const drummond = portfolios.filter(p => p.has_portfolio_data !== false);

  // code -> display name across the Drummond list and any loaded brand groups
  const nameByCode = {};
  portfolios.forEach(p => { nameByCode[p.portfolio_code] = p.name; });
  Object.values(brandPortfolios).forEach(list => (list || []).forEach(p => {
    nameByCode[p.portfolio_code] = p.name;
  }));
  const describe = (code) => (nameByCode[code] ? `${nameByCode[code]} (${code})` : code);

  const renderPortfolioRow = (portfolio) => {
    const isPublished = publishedSet.has(portfolio.portfolio_code);
    return (
      <label key={portfolio.portfolio_code} className="publish-portfolio-row">
        <input
          type="checkbox"
          checked={isPublished}
          onChange={() => togglePortfolio(portfolio.portfolio_code)}
        />
        <span className="publish-portfolio-name">{portfolio.name}</span>
        <span className="publish-portfolio-code">{portfolio.portfolio_code}</span>
      </label>
    );
  };

  const renderGroupToggle = (portfolioList) => {
    const allPublished = portfolioList.every(p => publishedSet.has(p.portfolio_code));
    const nonePublished = portfolioList.every(p => !publishedSet.has(p.portfolio_code));
    return (
      <div className="publish-group-actions">
        {!allPublished && (
          <button
            type="button"
            className="btn-publish-all"
            onClick={(e) => { e.stopPropagation(); toggleAll(portfolioList, true); }}
          >
            Select All
          </button>
        )}
        {!nonePublished && (
          <button
            type="button"
            className="btn-publish-none"
            onClick={(e) => { e.stopPropagation(); toggleAll(portfolioList, false); }}
          >
            Deselect All
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="publish-modal-overlay" onClick={() => onClose(false)}>
      <div className="publish-modal" onClick={e => e.stopPropagation()}>
        <div className="publish-modal-header">
          <h3>Manage Publishing</h3>
          <select
            className="publish-period-select"
            value={selectedQuarter || ''}
            onChange={(e) => handleQuarterChange(e.target.value)}
            disabled={saving}
          >
            {quarterOptions.map(q => (
              <option key={q} value={q}>{formatPeriodLabel(q)}</option>
            ))}
          </select>
          <button className="publish-modal-close" onClick={() => onClose(false)}>&times;</button>
        </div>

        <div className="publish-modal-body">
          {loading ? (
            <div className="publish-loading">Loading portfolios...</div>
          ) : confirmDiff ? (
            <div className="publish-confirm">
              <p className="publish-confirm-title">
                Confirm publishing changes for {formatPeriodLabel(selectedQuarter)}
              </p>
              {confirmDiff.added.length > 0 && (
                <div className="publish-confirm-section">
                  <p className="publish-confirm-lead">
                    <span className="publish-history-action publish">Publish</span>
                    This will make the following {confirmDiff.added.length === 1 ? 'report' : `${confirmDiff.added.length} reports`} visible
                    to the relevant clients for the first time for this period.
                  </p>
                  <ul>
                    {confirmDiff.added.map(code => <li key={code}>{describe(code)}</li>)}
                  </ul>
                </div>
              )}
              {confirmDiff.removed.length > 0 && (
                <div className="publish-confirm-section">
                  <p className="publish-confirm-lead">
                    <span className="publish-history-action unpublish">Unpublish</span>
                    This will hide the following {confirmDiff.removed.length === 1 ? 'report' : `${confirmDiff.removed.length} reports`} from
                    clients. They are currently published for this period.
                  </p>
                  <ul>
                    {confirmDiff.removed.map(code => <li key={code}>{describe(code)}</li>)}
                  </ul>
                </div>
              )}
              <p className="publish-confirm-note">
                Reports not listed here are unchanged. Are you sure you want to proceed?
              </p>
              {saveError && <p className="publish-confirm-error">{saveError}</p>}
            </div>
          ) : (
            <>
              <p className="publish-info">
                Select which portfolios should be visible to clients for this period.
                {publishedSet.size > 0 && (
                  <span className="publish-count"> ({publishedSet.size} published)</span>
                )}
              </p>

              {/* Drummond portfolios */}
              <div className="publish-group">
                <div className="publish-group-header" onClick={() => toggleGroup('drummond')}>
                  <i className={`bi bi-chevron-${expandedGroups['drummond'] ? 'down' : 'right'} me-1`}></i>
                  <span className="publish-group-label">DRUMMOND PORTFOLIOS</span>
                  <span className="publish-group-count">
                    {drummond.filter(p => publishedSet.has(p.portfolio_code)).length}/{drummond.length}
                  </span>
                  {expandedGroups['drummond'] && renderGroupToggle(drummond)}
                </div>
                {expandedGroups['drummond'] && (
                  <div className="publish-group-items">
                    {drummond.map(renderPortfolioRow)}
                  </div>
                )}
              </div>

              {/* Brand groups */}
              {brands.map(brand => {
                const domain = brand.domain;
                const isExpanded = expandedGroups[domain];
                const domainPortfolios = (brandPortfolios[domain] || []).filter(p => p.has_portfolio_data !== false);
                const isLoadingDomain = loadingBrands[domain];

                return (
                  <div key={domain} className="publish-group">
                    <div className="publish-group-header" onClick={() => toggleGroup(domain)}>
                      <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                      <span className="publish-group-label">{brand.company_name.toUpperCase()}</span>
                      <span className="publish-group-count">{brand.portfolio_count}</span>
                      {isExpanded && domainPortfolios.length > 0 && renderGroupToggle(domainPortfolios)}
                    </div>
                    {isExpanded && (
                      <div className="publish-group-items">
                        {isLoadingDomain && <div className="publish-loading">Loading...</div>}
                        {domainPortfolios.map(renderPortfolioRow)}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Publish / unpublish audit trail for this period */}
              {showHistory && (
                <div className="publish-history">
                  <div className="publish-history-header">
                    Publishing history for {formatPeriodLabel(selectedQuarter)}
                  </div>
                  {loadingEvents ? (
                    <div className="publish-loading">Loading history...</div>
                  ) : events.length === 0 ? (
                    <div className="publish-loading">
                      No publishing events recorded for this period yet.
                    </div>
                  ) : (
                    <table className="publish-history-table">
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Who</th>
                          <th>Action</th>
                          <th>Portfolio</th>
                          <th>Via</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map(ev => (
                          <tr key={ev.id}>
                            <td className="publish-history-when">{formatEventTime(ev.created_at)}</td>
                            <td>{ev.user_email}</td>
                            <td>
                              <span className={`publish-history-action ${ev.action}`}>
                                {ev.action === 'publish' ? 'Published' : 'Unpublished'}
                              </span>
                            </td>
                            <td className="publish-portfolio-code">
                              {ev.portfolio_code || 'whole period'}
                            </td>
                            <td className="publish-history-source">{ev.source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="publish-modal-footer">
          <button
            type="button"
            className="btn-publish-history"
            onClick={() => setShowHistory(v => !v)}
            disabled={loading}
          >
            <i className={`bi bi-clock-history me-1`}></i>
            {showHistory ? 'Hide history' : 'History'}
          </button>
          <span className="publish-footer-spacer" />
          {confirmDiff ? (
            <>
              <button
                className="btn-cancel"
                onClick={() => { setConfirmDiff(null); setSaveError(null); }}
                disabled={saving}
              >
                Back
              </button>
              <button
                className="btn-save-publish"
                onClick={confirmSave}
                disabled={saving}
              >
                {saving ? 'Publishing...' : 'Yes, apply changes'}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-cancel"
                onClick={() => onClose(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn-save-publish"
                onClick={requestSave}
                disabled={saving || loading || !hasChanges()}
              >
                Review &amp; Publish
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishModal;
